import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEnGB from '@angular/common/locales/en-GB';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  Router,
  TitleStrategy,
  withComponentInputBinding,
} from '@angular/router';

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { EnvInterceptor, SEU_MOCK_INTERCEPTOR } from '@seu-interceptors';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  MissingTranslationHandler,
  provideTranslateService,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { SEU_ENVIRONMENT } from '@seu-environment';
import { getMSALRedirectUrl, SeuMissingTranslationHandler } from '@seu-helpers';
import { CustomTitleResolver } from '@seu-resolver';
import { SEU_LANGUAGE_DEFAULT } from '@seu-types';
import { SEU_APP_ROUTES } from './app.routing';
import { BASE_PATH } from './shared/api';

// Register locale data for Angular pipes
registerLocaleData(localeDe, 'de-DE');
registerLocaleData(localeEnGB, 'en-GB');

export function loggerCallback(logLevel: LogLevel, message: string): void {
  // eslint-disable-next-line no-console
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: SEU_ENVIRONMENT.msal.clientId,
      authority: SEU_ENVIRONMENT.msal.authority,
      redirectUri: getMSALRedirectUrl(SEU_ENVIRONMENT),
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(SEU_ENVIRONMENT.apiUrl, [
    `${SEU_ENVIRONMENT.msal.clientId}/.default`,
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    strictMatching: false,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [`${SEU_ENVIRONMENT.msal.clientId}/.default`],
    },
  };
}

export const SEU_APP_CONFIG: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(SEU_APP_ROUTES, withComponentInputBinding()),
    provideAnimationsAsync(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EnvInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: LOCALE_ID,
      useValue: SEU_LANGUAGE_DEFAULT,
    },
    {
      provide: TitleStrategy,
      useClass: CustomTitleResolver,
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([SEU_MOCK_INTERCEPTOR])
    ),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: SeuMissingTranslationHandler,
      },
    }),

    SEU_ENVIRONMENT.sentryEnabled
      ? {
          provide: ErrorHandler,
          useValue: Sentry.createErrorHandler({
            showDialog: false,
          }),
        }
      : [],
    SEU_ENVIRONMENT.sentryEnabled
      ? {
          provide: Sentry.TraceService,
          deps: [Router],
        }
      : [],
    SEU_ENVIRONMENT.sentryEnabled
      ? {
          provide: APP_INITIALIZER,
          useFactory: () => () => {},
          deps: [Sentry.TraceService],
          multi: true,
        }
      : [],
    {
      provide: BASE_PATH,
      useValue: SEU_ENVIRONMENT.apiUrl,
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
  ],
};
